/**
 * Client-side resize/compress for car photos (§7).
 *
 * The byte cap is enforced at S3 by the presigned policy's
 * `content-length-range` condition, so a client that lies or skips this step
 * simply gets a rejection from S3 — this module is not a security control. What
 * it is: the thing that stops a 6 MB phone photo becoming a failed upload the
 * user cannot diagnose. The cap exists for cost (§7: "Bounding bytes per image
 * keeps storage and transfer costs negligible"), and the only way to honour it
 * without asking racers to open an image editor is to do the work here.
 *
 * The compression loop is deliberately split from the pixel work. `compress()`
 * takes an `Encoder` and knows nothing about canvases; the real encoder is
 * fifteen lines at the bottom. That is what lets the cap be tested for real —
 * jsdom has no canvas, so a test that needed one could only ever assert that
 * the code was called, not that the output actually fits.
 */

import { IMAGE_MAX_BYTES } from '@svcdue/contracts';

/**
 * Longest edge, in pixels.
 *
 * A car photo is displayed at a few hundred CSS pixels; 1600 leaves headroom
 * for a 2× display and a future lightbox without paying for the 4032px a modern
 * phone actually shoots. Starting here means the quality ladder below usually
 * lands on the first rung.
 */
export const MAX_EDGE = 1600;

/**
 * Quality rungs, tried in order.
 *
 * Stops at 0.4: below that JPEG artefacts are obvious on the flat body panels
 * and chrome that car photos are mostly made of. If 0.4 still will not fit, the
 * honest fix is fewer pixels, not worse ones — hence the scale-down below.
 */
const QUALITY_LADDER = [0.82, 0.7, 0.6, 0.5, 0.4] as const;

/** How much to shrink by when the whole ladder failed at the current size. */
const SCALE_STEP = 0.75;

/**
 * Bounded so a pathological input cannot spin forever. Four rounds takes 1600px
 * down to ~500px; anything that still will not fit under the cap at 500px and
 * 40% quality is not a photograph.
 */
const MAX_SCALE_ROUNDS = 4;

/**
 * The pixel operations, injected.
 *
 * Two implementations exist: `canvasEncoder` (the browser) and whatever a test
 * hands in. The interface is the seam that makes the loop testable.
 */
export interface Encoder<Source> {
  decode(file: Blob): Promise<{ source: Source; width: number; height: number }>;
  encode(source: Source, width: number, height: number, type: string, quality: number): Promise<Blob>;
  /** Free anything the decode held. `ImageBitmap` needs it; a fake does not. */
  release?(source: Source): void;
}

export interface CompressOptions {
  maxBytes?: number;
  maxEdge?: number;
  /** `image/webp` where supported — typically 25-35% smaller than JPEG at parity. */
  type?: string;
}

export interface CompressResult {
  blob: Blob;
  width: number;
  height: number;
  quality: number;
  type: string;
}

export class ImageTooLargeError extends Error {
  constructor(maxBytes: number) {
    super(`Could not get this image under ${Math.floor(maxBytes / 1024)} KB. Try a smaller photo.`);
    this.name = 'ImageTooLargeError';
  }
}

/** Fit within a square bound, preserving aspect ratio. Never enlarges. */
export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  // Upscaling a small photo would add bytes and no detail.
  if (longest <= maxEdge) return { width, height };

  const scale = maxEdge / longest;
  // Floor, not round: rounding up can exceed `maxEdge` by a pixel, and a zero
  // dimension from a degenerate input would make canvas throw.
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)) };
}

/**
 * Squeeze an image under the cap: drop quality first, pixels only if forced.
 *
 * The order matters. Quality is nearly free perceptually down to ~0.6 on a
 * photo, whereas resolution is what makes the picture legible on a car card, so
 * every rung of the quality ladder is spent before a single pixel is given up.
 */
export async function compress<Source>(
  file: Blob,
  encoder: Encoder<Source>,
  options: CompressOptions = {},
): Promise<CompressResult> {
  const maxBytes = options.maxBytes ?? IMAGE_MAX_BYTES;
  const maxEdge = options.maxEdge ?? MAX_EDGE;
  const type = options.type ?? 'image/jpeg';

  const { source, width, height } = await encoder.decode(file);

  try {
    let target = fitWithin(width, height, maxEdge);

    for (let round = 0; round <= MAX_SCALE_ROUNDS; round++) {
      for (const quality of QUALITY_LADDER) {
        const blob = await encoder.encode(source, target.width, target.height, type, quality);
        if (blob.size <= maxBytes) {
          return { blob, width: target.width, height: target.height, quality, type };
        }
      }
      // Whole ladder failed at this size — take pixels off and start again.
      target = {
        width: Math.max(1, Math.floor(target.width * SCALE_STEP)),
        height: Math.max(1, Math.floor(target.height * SCALE_STEP)),
      };
    }

    throw new ImageTooLargeError(maxBytes);
  } finally {
    encoder.release?.(source);
  }
}

// --- the browser implementation ------------------------------------------

/**
 * `image/webp` is not in every browser Safari ships, and the API only accepts
 * jpeg/webp/png (`IMAGE_CONTENT_TYPES`). Ask the canvas what it can actually
 * produce rather than sniffing a user agent: a canvas that cannot encode webp
 * silently hands back a PNG, which for a photo is *larger* than the original —
 * exactly the wrong outcome for a byte cap.
 */
export function bestSupportedType(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    if (canvas.toDataURL('image/webp').startsWith('data:image/webp')) return 'image/webp';
  } catch {
    // Fall through to JPEG, which is universal.
  }
  return 'image/jpeg';
}

export const canvasEncoder: Encoder<ImageBitmap> = {
  async decode(file) {
    // `createImageBitmap` applies EXIF orientation, which `new Image()` does
    // not — without it, photos shot in portrait upload sideways.
    const source = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source, width: source.width, height: source.height };
  },

  encode(source, width, height, type, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process this image');
    ctx.drawImage(source, 0, 0, width, height);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not process this image'))),
        type,
        quality,
      );
    });
  },

  release(source) {
    source.close();
  },
};

/** Resize a user-picked file for upload. The browser-wired entry point. */
export function prepareCarPhoto(file: Blob): Promise<CompressResult> {
  return compress(file, canvasEncoder, { type: bestSupportedType() });
}

/**
 * Send the resized bytes to S3.
 *
 * **Field order is not stylistic — S3 requires it.** The policy fields must all
 * precede `file` in the multipart body, because S3 parses the form as a stream
 * and stops reading at the file part: anything after it is ignored, so a `key`
 * sent last is a `key` S3 never sees. It answers with a 403 that names none of
 * this.
 *
 * Note also that this goes straight to S3, not through our API, and must carry
 * **no `Authorization` header** — the presigned policy is the credential, and an
 * unexpected auth header makes S3 reject the request outright.
 */
export async function uploadToS3(url: string, fields: Record<string, string>, blob: Blob): Promise<void> {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  form.append('file', blob);

  const response = await fetch(url, { method: 'POST', body: form });
  if (!response.ok) {
    // S3 answers in XML, which is no use to a racer holding a phone. The likely
    // causes are an expired policy or a file over the cap, and both are fixed
    // by trying again.
    throw new Error('The photo could not be uploaded. Try again.');
  }
}
